import { Image as ImageIcon, Move, ScanLine } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getImagePlacement } from '../lib/layout'

interface Props {
  imageSrc: string | null
  imageWidthPx: number
  imageHeightPx: number
  cols: number
  rows: number
  cellMm: number
  zoom: number
  offsetXmm: number
  offsetYmm: number
  showGrid: boolean
  gridOpacity: number
  onTransform: (next: { zoom: number; offsetXmm: number; offsetYmm: number }) => void
  onPickImage: () => void
}

interface PointerPoint {
  x: number
  y: number
}

export default function ImageStage(props: Props) {
  const {
    imageSrc,
    imageWidthPx,
    imageHeightPx,
    cols,
    rows,
    cellMm,
    zoom,
    offsetXmm,
    offsetYmm,
    showGrid,
    gridOpacity,
    onTransform,
    onPickImage,
  } = props

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const pointersRef = useRef(new Map<number, PointerPoint>())
  const gestureRef = useRef<{ distance: number; center: PointerPoint } | null>(null)
  const transformRef = useRef({ zoom, offsetXmm, offsetYmm })
  const [canvasSize, setCanvasSize] = useState({ width: 1, height: 1 })

  const posterWidthMm = cols * cellMm
  const posterHeightMm = rows * cellMm

  useEffect(() => {
    transformRef.current = { zoom, offsetXmm, offsetYmm }
  }, [zoom, offsetXmm, offsetYmm])

  useEffect(() => {
    if (!imageSrc) {
      imageRef.current = null
      return
    }
    const image = new Image()
    image.onload = () => {
      imageRef.current = image
      setCanvasSize((size) => ({ ...size }))
    }
    image.src = imageSrc
  }, [imageSrc])

  // Canvas 的 CSS 尺寸由容器决定，像素尺寸按 devicePixelRatio 放大，避免手机高分屏预览发糊。
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return

    const update = () => {
      const bounds = wrap.getBoundingClientRect()
      const maxWidth = Math.max(260, bounds.width)
      const maxHeight = Math.min(window.innerHeight * 0.58, 720)
      const aspect = posterWidthMm / posterHeightMm
      let width = maxWidth
      let height = width / aspect
      if (height > maxHeight) {
        height = maxHeight
        width = height * aspect
      }
      setCanvasSize({ width: Math.max(1, width), height: Math.max(1, height) })
    }

    const observer = new ResizeObserver(update)
    observer.observe(wrap)
    update()
    return () => observer.disconnect()
  }, [posterWidthMm, posterHeightMm])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5)
    canvas.width = Math.round(canvasSize.width * dpr)
    canvas.height = Math.round(canvasSize.height * dpr)
    canvas.style.width = `${canvasSize.width}px`
    canvas.style.height = `${canvasSize.height}px`

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height)

    const image = imageRef.current
    if (image && imageWidthPx > 0 && imageHeightPx > 0) {
      const placement = getImagePlacement({
        imageWidthPx,
        imageHeightPx,
        posterWidthMm,
        posterHeightMm,
        zoom,
        offsetXmm,
        offsetYmm,
      })
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(
        image,
        (placement.leftMm / posterWidthMm) * canvasSize.width,
        (placement.topMm / posterHeightMm) * canvasSize.height,
        (placement.widthMm / posterWidthMm) * canvasSize.width,
        (placement.heightMm / posterHeightMm) * canvasSize.height,
      )
    }

    if (showGrid) {
      for (let c = 0; c <= cols; c += 1) {
        const x = (c / cols) * canvasSize.width
        const major = c % 5 === 0
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvasSize.height)
        ctx.strokeStyle = major
          ? `rgba(31, 37, 49, ${Math.min(0.92, gridOpacity + 0.18)})`
          : `rgba(45, 53, 68, ${gridOpacity})`
        ctx.lineWidth = major ? 1 : 0.55
        ctx.stroke()
      }
      for (let r = 0; r <= rows; r += 1) {
        const y = (r / rows) * canvasSize.height
        const major = r % 5 === 0
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvasSize.width, y)
        ctx.strokeStyle = major
          ? `rgba(31, 37, 49, ${Math.min(0.92, gridOpacity + 0.18)})`
          : `rgba(45, 53, 68, ${gridOpacity})`
        ctx.lineWidth = major ? 1 : 0.55
        ctx.stroke()
      }
    }
  }, [
    canvasSize,
    cols,
    rows,
    posterWidthMm,
    posterHeightMm,
    imageSrc,
    imageWidthPx,
    imageHeightPx,
    zoom,
    offsetXmm,
    offsetYmm,
    showGrid,
    gridOpacity,
  ])

  const emitTransform = useCallback(
    (next: { zoom: number; offsetXmm: number; offsetYmm: number }) => {
      transformRef.current = next
      onTransform(next)
    },
    [onTransform],
  )

  const getPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!imageSrc) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointersRef.current.set(event.pointerId, getPointer(event))
    gestureRef.current = null
  }

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!imageSrc || !pointersRef.current.has(event.pointerId)) return
    const oldPoint = pointersRef.current.get(event.pointerId)!
    const nextPoint = getPointer(event)
    pointersRef.current.set(event.pointerId, nextPoint)
    const pointers = [...pointersRef.current.values()]
    const current = transformRef.current

    if (pointers.length === 1) {
      const dx = nextPoint.x - oldPoint.x
      const dy = nextPoint.y - oldPoint.y
      emitTransform({
        ...current,
        offsetXmm: current.offsetXmm + (dx / canvasSize.width) * posterWidthMm,
        offsetYmm: current.offsetYmm + (dy / canvasSize.height) * posterHeightMm,
      })
      return
    }

    if (pointers.length >= 2) {
      const a = pointers[0]
      const b = pointers[1]
      const distance = Math.hypot(a.x - b.x, a.y - b.y)
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const previous = gestureRef.current

      if (!previous) {
        gestureRef.current = { distance, center }
        return
      }

      const ratio = Math.min(1.16, Math.max(0.86, distance / Math.max(1, previous.distance)))
      const nextZoom = Math.min(8, Math.max(0.35, current.zoom * ratio))
      const oldPlacement = getImagePlacement({
        imageWidthPx,
        imageHeightPx,
        posterWidthMm,
        posterHeightMm,
        zoom: current.zoom,
        offsetXmm: current.offsetXmm,
        offsetYmm: current.offsetYmm,
      })
      const posterPinchX = (center.x / canvasSize.width) * posterWidthMm
      const posterPinchY = (center.y / canvasSize.height) * posterHeightMm
      const scaleRatio = nextZoom / current.zoom
      const nextWidth = oldPlacement.widthMm * scaleRatio
      const nextHeight = oldPlacement.heightMm * scaleRatio
      const nextLeft = posterPinchX - (posterPinchX - oldPlacement.leftMm) * scaleRatio
      const nextTop = posterPinchY - (posterPinchY - oldPlacement.topMm) * scaleRatio
      const centerDxMm = ((center.x - previous.center.x) / canvasSize.width) * posterWidthMm
      const centerDyMm = ((center.y - previous.center.y) / canvasSize.height) * posterHeightMm

      emitTransform({
        zoom: nextZoom,
        offsetXmm: nextLeft - (posterWidthMm - nextWidth) / 2 + centerDxMm,
        offsetYmm: nextTop - (posterHeightMm - nextHeight) / 2 + centerDyMm,
      })
      gestureRef.current = { distance, center }
    }
  }

  const endPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pointersRef.current.delete(event.pointerId)
    gestureRef.current = null
  }

  return (
    <section className="stage-card" aria-label="图片对齐预览">
      <div className="stage-toolbar">
        <div>
          <strong>对齐画布</strong>
          <span>{imageSrc ? '拖动图片 · 双指缩放' : '先导入一张图片'}</span>
        </div>
        <div className="stage-size">
          <ScanLine size={15} />
          {posterWidthMm.toFixed(1)} × {posterHeightMm.toFixed(1)} mm
        </div>
      </div>

      <div className="canvas-wrap" ref={wrapRef}>
        {imageSrc ? (
          <canvas
            ref={canvasRef}
            className="preview-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            aria-label="图片与格网预览，可拖动与双指缩放"
          />
        ) : (
          <button className="empty-stage" type="button" onClick={onPickImage}>
            <span className="empty-icon"><ImageIcon size={26} /></span>
            <strong>导入图片开始</strong>
            <span>PNG / JPG / WebP · 图片只在本机浏览器处理</span>
          </button>
        )}
      </div>

      {imageSrc && (
        <div className="stage-hint">
          <Move size={14} />
          手机上直接拖动位置；双指捏合缩放。桌面端也可配合右侧缩放滑杆精调。
        </div>
      )}
    </section>
  )
}
